/**
 * File: PushTrace.tsx
 * Path: apps/admin/src/features/diagnostics/components/PushTrace.tsx
 * Description: The live notification test: sends one test notification and
 * follows it step by step, with timings: the server sending it to each
 * device, the bell saving it, and each device confirming it was shown. The
 * step where it stops is where the problem is.
 */

import React from "react";
import { Loader2 } from "lucide-react";
import type { PushDevice } from "@idk/config";
import { Button } from "@idk/ui";
import { StatusMark } from "./CheckList";
import { usePushTraceController } from "@/features/diagnostics/hooks/usePushTraceController";

export const PushTrace: React.FC<{ devices: readonly PushDevice[] }> = ({ devices }) => {
  const { hasRun, sending, steps, start } = usePushTraceController(devices);

  return (
    <div className="space-y-stack-md">
      <div className="flex flex-wrap items-center gap-stack-sm">
        <Button size="sm" onClick={start} disabled={sending || devices.length === 0}>
          {sending && <Loader2 className="mr-stack-xs h-4 w-4 animate-spin" aria-hidden />}
          {hasRun ? "Send another test" : "Send a test notification"}
        </Button>
        {devices.length === 0 && (
          <p className="text-sm text-muted-foreground">Set up a device in Settings &gt; Notifications first.</p>
        )}
      </div>
      {steps.length > 0 && (
        <ol className="space-y-stack-xs" aria-live="polite">
          {steps.map((step) => (
            <li key={step.id} className="flex items-start gap-stack-sm text-sm" data-status={step.status}>
              {step.status === "waiting" ? (
                <Loader2 className="h-4 w-4 shrink-0 translate-y-px animate-spin text-muted-foreground" aria-label="Waiting" role="img" />
              ) : (
                <StatusMark status={step.status} className="translate-y-px" />
              )}
              <span className="space-y-stack-xs">
                <span className="block">{step.text}</span>
                {step.tip && <span className="block text-muted-foreground">{step.tip}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
